
"use client";

import type { User as FirebaseUser } from "firebase/auth";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from "firebase/firestore"; // Added Timestamp
import { auth, db } from "@/lib/firebase";
import type { UserProfile, UserRole } from "@/types"; // Ensure UserRole is imported

// A list of valid roles for runtime checking if needed, though typeof check is primary here
const validUserRoles: UserRole[] = ["Owner", "Admin", "Supervisor", "Agent", "Client"];

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isSupervisor: boolean;
  isAgent: boolean;
  logout: () => Promise<void>;
  updateUserProfileInContext: (profile: UserProfile) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const profileDataFromDb = userDocSnap.data();
            // Validate essential fields before setting the profile
            if (profileDataFromDb &&
                typeof profileDataFromDb.uid === 'string' &&
                profileDataFromDb.uid === user.uid && // Ensure UID in doc matches authenticated user
                typeof profileDataFromDb.role === 'string' &&
                validUserRoles.includes(profileDataFromDb.role as UserRole) // Check if role is a valid UserRole
            ) {
              setUserProfile(profileDataFromDb as UserProfile);
            } else {
              console.warn(
                `Firestore document for UID ${user.uid} exists but is missing essential fields (uid, role), has a mismatched UID, or an invalid role. Treating as missing/invalid profile. Data:`,
                profileDataFromDb
              );
              setUserProfile(null); // Set to null if data is invalid or mismatched
            }
          } else {
            // Profile doesn't exist, attempt to create a default one
            console.warn(`User profile for UID ${user.uid} not found in Firestore. Attempting to create a default profile.`);
            const profileToCreateInDb = { 
              uid: user.uid,
              email: user.email || "unknown@example.com",
              displayName: user.displayName || user.email?.split('@')[0] || "New User",
              role: "Agent" as UserRole,
              companyName: "",
              status: "Pending" as UserProfile["status"],
              createdAt: serverTimestamp(),
            };
            try {
              await setDoc(userDocRef, profileToCreateInDb);
              const localProfileForContext: UserProfile = {
                uid: user.uid,
                email: profileToCreateInDb.email,
                displayName: profileToCreateInDb.displayName,
                role: profileToCreateInDb.role,
                companyName: profileToCreateInDb.companyName,
                status: profileToCreateInDb.status,
                createdAt: new Timestamp(Math.floor(Date.now() / 1000), 0) 
              };
              setUserProfile(localProfileForContext);
              console.log(`Default profile created for UID ${user.uid}.`);
            } catch (errorCreatingProfile) {
              console.error(`Failed to create default profile in Firestore for UID ${user.uid}:`, errorCreatingProfile);
              setUserProfile(null); 
            }
          }
        } catch (error) {
          console.error(`Error fetching or processing user profile for UID ${user.uid}:`, error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const updateUserProfileInContext = (profile: UserProfile) => {
    setUserProfile(profile);
  };

  const logout = async () => {
    setLoading(true);
    await auth.signOut();
    setCurrentUser(null);
    setUserProfile(null);
    // setLoading(false) will be called by onAuthStateChanged
  };

  const isOwner = userProfile?.role === "Owner";
  const isAdmin = userProfile?.role === "Admin" || isOwner;
  const isSupervisor = userProfile?.role === "Supervisor" || isAdmin;
  const isAgent = userProfile?.role === "Agent";

  return (
    <AuthContext.Provider value={{ currentUser, userProfile, loading, isOwner, isAdmin, isSupervisor, isAgent, logout, updateUserProfileInContext }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
